import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

import DrawerField from "components/Drawer";
import CaptivePortalSuccessRate from "./components/CaptivePortalSuccessRate";
import CaptivePortalAuthTime from "./components/CaptivePortalAuthTime";
import CaptivePortalAccessSatisfactionScore from "./components/CaptivePortalAccessSatisfactionScore";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import Api from "./util/api";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const ServiceExperience = () => {
  const { auth, isAuthReady } = useAuthScope();
  const { sendBatch } = useDataSourceManager();

  const [activeDrawer, setActiveDrawer] = useState(null); // 'success' | 'auth' | 'satisfaction' | null
  const [loading, setLoading] = useState(true);

  const [successRateData, setSuccessRateData] = useState(null);
  const [authTimeData, setAuthTimeData] = useState(null);
  const [satisfactionData, setSatisfactionData] = useState(null);

  const [requestUuids] = useState({
    successRate: uuidv4(),
    authTime: uuidv4(),
    satisfaction: uuidv4(),
  });

  const isAllZeros = (data) => {
    if (!data) return true;
    return !(data.apCount > 0);
  };

  const fetchData = async () => {
    if (checkMockup === "false" && (!auth.realm || (!auth.orgs && !auth.sites))) return;

    setLoading(true);
    const api = Api.init(auth);

    const successRateParams = api.getConnectSuccessRateParams();
    const authTimeParams = api.getConnectTimeParams();
    const satisfactionParams = api.getGuestAccessHappinessParams();

    const { getSendBatchData } = await sendBatch([
      { ...successRateParams, requestUuid: requestUuids.successRate },
      { ...authTimeParams, requestUuid: requestUuids.authTime },
      { ...satisfactionParams, requestUuid: requestUuids.satisfaction },
    ]);

    const successData = getSendBatchData({ requestUuid: requestUuids.successRate })?.data;
    const timeData = getSendBatchData({ requestUuid: requestUuids.authTime })?.data;
    const happinessData = getSendBatchData({ requestUuid: requestUuids.satisfaction })?.data;

    if (successData) setSuccessRateData(successData);
    if (timeData) setAuthTimeData(timeData);
    if (happinessData) setSatisfactionData(happinessData);

    setLoading(false);
  };

  useEffect(() => {
    if (isAuthReady) {
      fetchData();
    }
  }, [isAuthReady]);

  const handleCloseDrawer = () => {
    setActiveDrawer(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <CaptivePortalSuccessRate
        chartData={successRateData}
        isEmpty={isAllZeros(successRateData)}
        onClick={() => setActiveDrawer('success')}
        loading={loading}
      />

      <CaptivePortalAuthTime
        chartData={authTimeData}
        isEmpty={isAllZeros(authTimeData)}
        onClick={() => setActiveDrawer('auth')}
        loading={loading}
      />

      <CaptivePortalAccessSatisfactionScore
        chartData={satisfactionData}
        isEmpty={isAllZeros(satisfactionData)}
        onClick={() => setActiveDrawer('satisfaction')}
        loading={loading}
      />

      {/* Success Rate Details Drawer */}
      {activeDrawer === 'success' && (
        <DrawerField
          open={true}
          handleClose={handleCloseDrawer}
          width="550px"
          contentKey="CaptivePortalSuccessRateDetails"
          commonContent="ainsightSuccessRateDrawer"
          contentProps={{
            chartData: successRateData,
            auth: auth
          }}
        />
      )}

      {/* Auth Time Details Drawer */}
      {activeDrawer === 'auth' && (
        <DrawerField
          open={true}
          handleClose={handleCloseDrawer}
          width="550px"
          contentKey="CaptivePortalAuthTimeDetails"
          commonContent="ainsightAuthTimeDrawer"
          contentProps={{
            chartData: authTimeData,
            auth: auth
          }}
        />
      )}

      {/* Satisfaction Score Details Drawer */}
      {activeDrawer === 'satisfaction' && (
        <DrawerField
          open={true}
          handleClose={handleCloseDrawer}
          width="550px"
          contentKey="GuestAccessSatisfactionDetails"
          commonContent="ainsightSatisfactionDrawer"
          contentProps={{
            chartData: satisfactionData,
            auth: auth
          }}
        />
      )}
    </Box>
  );
};

export default ServiceExperience;
